import { useMemo } from 'react'
import { useWorkspaceNavigate } from '../common/workspaceNavigation'
import { openSafeExternalUrl } from '../common/safeExternalUrl'
import type { HubSearchOptions } from '../common/hubSearch'
import { openPolicyReader, policyIdFromPath } from '../common/policyReader'
import { useUserPermissions } from '../contexts/UserPermissionsContext'
import HomeSearch from '../features/home-preview/HomeSearch'
import { useHomeResources } from '../features/home-preview/useHomeResources'
import { searchResources } from '../features/home-preview/searchResources'
import { readSavedPaths, type Resource } from '../features/home-preview/model'
import '../features/home-preview/home-preview.css'

type Props = { isOpen: boolean; onClose: () => void; options?: HubSearchOptions }

function SearchContent({ onClose, options }: Omit<Props, 'isOpen'>) {
  const { effectiveUserEmail, userPermissions, isOverride } = useUserPermissions()
  const email = effectiveUserEmail || ''
  const catalogue = useHomeResources(email)
  const resources = useMemo(() => searchResources(catalogue, userPermissions, email, isOverride),
    [catalogue.resources, catalogue.knowledge, catalogue.ready, catalogue.accessFlags, catalogue.documentsVisible, userPermissions, email, isOverride])
  const navigate = useWorkspaceNavigate()
  const open = (resource: Resource) => {
    const key = `home-preview:recent:${email}`
    try { localStorage.setItem(key, JSON.stringify([resource.path, ...(readSavedPaths(key) || []).filter(path => path !== resource.path)].slice(0, 8))) }
    catch { /* Optional browsing history must not prevent navigation. */ }
    onClose()
    const policyId = policyIdFromPath(resource.path)
    // Policies open in place, over whatever page the search started from.
    if (policyId) openPolicyReader(policyId)
    else if (/^https?:/i.test(resource.path)) openSafeExternalUrl(resource.path)
    else navigate(resource.path)
  }
  return <HomeSearch initialQuery={options?.query || ''} initialCategory={options?.category}
    resources={resources} email={email} ready={catalogue.ready} flags={catalogue.accessFlags}
    documentsVisible={catalogue.documentsVisible}
    loading={catalogue.loading || catalogue.knowledgeLoading} errors={catalogue.errors}
    onOpen={open} onClose={onClose} />
}

export default function GlobalSearchModal({ isOpen, ...props }: Props) {
  return isOpen ? <SearchContent {...props} /> : null
}
